/**
 * 생성기 입력창 위 안내 한 줄 — 방금 생긴 안내(첨부 검사·실행 결과)와 게이트가 거는 상시 배너(게스트 로그인·
 * 무료 소진 업그레이드) 중 무엇을 보일지 정한다. 실행 버튼을 막는 이유(툴팁)도 같은 게이트에서 나온다.
 */
import { useState } from 'react';

import { trackEvent } from '@zep/logger/tracking';

import { usePremiumAccess } from '@/shared/plan';

import { type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';
import {
  type AiGenerateDockNoticeData,
  type AiGenerateDockRunCopy,
} from '../types/aiGenerateDock.types';
import {
  type AiGenerateDockGate,
  type AiGenerateDockGateKind,
} from '../utils/aiGenerateDockGate';

/** 로그인 뒤 같은 빌드 화면으로 돌아오게 지금 주소를 넘긴다(채팅 인증 만료 처리와 같은 경로). */
const redirectToAiGenerateLogin = () => {
  const callbackPath = `${window.location.pathname}${window.location.search}`;
  window.location.assign(
    `/login?callbackUrl=${encodeURIComponent(callbackPath)}`,
  );
};

/** 방금 생긴 안내는 그 안내가 생긴 탭에서만 보인다. */
type RecentNotice = {
  tab: AiGenerateDockSourceType;
  notice: AiGenerateDockNoticeData;
};

export type AiGenerateDockNotice = {
  /** 입력창 위에 보일 안내. 없으면 null. */
  notice: AiGenerateDockNoticeData | null;
  /** 실행 버튼을 막는 이유 — 게이트가 열려 있으면 undefined. */
  submitBlockedReason: string | undefined;
  /** 방금 생긴 안내를 그 탭(기본: 지금 탭)에 건다. */
  showNotice: (
    notice: AiGenerateDockNoticeData,
    tab?: AiGenerateDockSourceType,
  ) => void;
  clearNotice: () => void;
};

export const useAiGenerateDockNotice = ({
  gate,
  sourceType,
  runCopy,
}: {
  gate: AiGenerateDockGate;
  sourceType: AiGenerateDockSourceType;
  runCopy: AiGenerateDockRunCopy;
}): AiGenerateDockNotice => {
  const { openFeaturePaywall } = usePremiumAccess();
  const [recent, setRecent] = useState<RecentNotice | null>(null);
  const recentNotice = recent?.tab === sourceType ? recent.notice : null;

  const gateBlockedReason: Record<AiGenerateDockGateKind, string | undefined> =
    {
      open: undefined,
      guest: runCopy.guestBanner,
      chatBusy: runCopy.chatBusy,
      quotaExhausted: runCopy.quotaExceeded,
    };
  // 게이트가 거는 상시 배너 — 채팅 응답 중은 배너 없이 실행 버튼 툴팁으로만 알린다.
  const gateNotice: AiGenerateDockNoticeData | null =
    gate.kind === 'guest'
      ? {
          tone: 'login',
          message: runCopy.guestBanner,
          action: {
            label: runCopy.login,
            onClick: () => {
              trackEvent('quiz_ai_generator_login_click', {
                place: 'guest_banner',
                source_type: sourceType,
                destination: 'login',
              });
              redirectToAiGenerateLogin();
            },
          },
        }
      : gate.kind === 'quotaExhausted'
      ? {
          tone: 'plan',
          message: runCopy.quotaExhaustedBanner,
          action: {
            label: runCopy.upgrade,
            onClick: () =>
              void openFeaturePaywall({
                requiredPlan: 'BASIC',
                from: 'aiBuilder',
                // 채팅의 소진 카드와 같은 from 이라 페이월 결과 이벤트에서 생성기를 가른다.
                trackingSource: 'aiGeneratorDock',
              }),
          },
        }
      : null;

  return {
    // 방금 생긴 안내(첨부 검사·실행 결과)가 게이트 배너보다 앞선다.
    // 무료 소진이면 업그레이드 버튼이 있는 게이트 배너가 방금 생긴 안내(버튼 없는 소진 문구)보다 앞선다.
    notice:
      gate.kind === 'quotaExhausted' ? gateNotice : recentNotice ?? gateNotice,
    submitBlockedReason: gateBlockedReason[gate.kind],
    showNotice: (notice, tab = sourceType) => setRecent({ tab, notice }),
    clearNotice: () => setRecent(null),
  };
};
